/**
 * Subir una carpeta a Inbox (GEN-EKB-041).
 *
 * El kernel ya admite rutas con subcarpetas en Inbox (`POST /v1/files/inbox?filename=&path=`), así que subir una carpeta es
 * subir cada fichero con su ruta relativa en `path`. Aquí vive todo lo que el navegador pone distinto según de dónde venga la carpeta:
 *   - elegida con `<input webkitdirectory>`: cada File trae `webkitRelativePath` («carpeta/sub/a.md»);
 *   - arrastrada: hay que recorrer los `FileSystemEntry` del `DataTransfer` (el File no trae ruta).
 * Un «item» es `{ file, ruta }`, con `ruta` relativa y con la carpeta elegida como primer segmento («procedimientos/anexos/a.md»);
 * un fichero suelto tiene `ruta === file.name`.
 */

/** Ficheros del sistema que no son material de nadie: no se suben ni cuentan. */
export const esOculto = (nombre) => nombre.startsWith('.') || nombre.toLowerCase() === 'thumbs.db'
const ocultaLaRuta = (ruta) => ruta.split('/').some(esOculto)

/** {dir, nombre} de una ruta relativa: «a/b/c.md» → {dir: 'a/b', nombre: 'c.md'}. */
export function partirRuta(ruta) {
  const i = ruta.lastIndexOf('/')
  return i < 0 ? { dir: '', nombre: ruta } : { dir: ruta.slice(0, i), nombre: ruta.slice(i + 1) }
}

/** Items de los File de un `<input>` (suelto o con webkitdirectory), sin ficheros ocultos. */
export function itemsDeLista(files) {
  return [...(files ?? [])]
    .map(file => ({ file, ruta: file.webkitRelativePath || file.name }))
    .filter(it => !ocultaLaRuta(it.ruta))
}

const archivoDe = (entry) => new Promise((ok, ko) => entry.file(ok, ko))

async function hijosDe(entry) {
  const lector = entry.createReader()
  const todos = []
  // readEntries devuelve a trozos (~100): se repite hasta que viene vacío.
  for (;;) {
    const lote = await new Promise((ok, ko) => lector.readEntries(ok, ko))
    if (!lote.length) return todos
    todos.push(...lote)
  }
}

async function recorrer(entry, prefijo, salida) {
  if (esOculto(entry.name)) return
  if (entry.isFile) {
    salida.push({ file: await archivoDe(entry), ruta: prefijo + entry.name })
  } else if (entry.isDirectory) {
    for (const hijo of await hijosDe(entry)) await recorrer(hijo, `${prefijo}${entry.name}/`, salida)
  }
}

/**
 * Items de lo que se suelta (`event.dataTransfer`), carpetas incluidas. Los `FileSystemEntry` se toman ANTES de cualquier `await`:
 * el navegador vacía el DataTransfer al terminar el manejador del evento.
 */
export async function itemsDeDrop(dataTransfer) {
  const entradas = [...(dataTransfer?.items ?? [])]
    .filter(it => it.kind === 'file')
    .map(it => (typeof it.webkitGetAsEntry === 'function' ? it.webkitGetAsEntry() : null))
  const sueltos = [...(dataTransfer?.files ?? [])]
  if (!entradas.length || entradas.some(e => !e)) return itemsDeLista(sueltos)  // sin API de carpetas: solo ficheros
  const salida = []
  for (const e of entradas) await recorrer(e, '', salida)
  return salida
}

/** ¿El arrastre lleva ficheros (y no texto, enlaces...)? */
export const arrastraFicheros = (dataTransfer) => [...(dataTransfer?.types ?? [])].includes('Files')

/** Separa los items en sueltos y carpetas (por su primer segmento): {sueltos: [item], carpetas: [{nombre, items}]}. */
export function agrupar(items) {
  const sueltos = []
  const porCarpeta = new Map()
  for (const it of items) {
    const i = it.ruta.indexOf('/')
    if (i < 0) { sueltos.push(it); continue }
    const nombre = it.ruta.slice(0, i)
    if (!porCarpeta.has(nombre)) porCarpeta.set(nombre, [])
    porCarpeta.get(nombre).push(it)
  }
  return { sueltos, carpetas: [...porCarpeta].map(([nombre, its]) => ({ nombre, items: its })) }
}

/**
 * Sube un item conservando su ruta relativa bajo `base`. `subir` es `uploadInboxFile`. Un fichero suelto sin base se sube
 * como siempre (sin `path`), para no cambiar nada de lo que ya funciona.
 */
export function subirItem(subir, zona, item, base = '') {
  const { dir } = partirRuta(item.ruta)
  const path = [base, dir].filter(Boolean).join('/')
  return path ? subir(zona, item.file, path) : subir(zona, item.file)
}
